/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RadioGroup } from '@/components/forms/radio-group';
import { useCurrency } from '@/providers/currency';
import { formatCurrency } from '@/lib/currency';
import { cn } from '@/lib/utils';
import RadioDeliveryMethodTile from './radio-delivery-methods-tile';

/** Minimal shipping-method shape consumed from the basket's applicable methods. */
export interface ShippingMethodOption {
    id: string;
    name: string;
    description?: string;
    price: number;
}

export interface ReduceShippingMethodsProps {
    /** Applicable shipping methods, in display order. */
    methods: ShippingMethodOption[];
    /** Native form field name so the choice posts inside an enclosing `<form>`. */
    name?: string;
    /** Initially selected method id (uncontrolled). */
    defaultValue?: string;
    /** Controlled selected method id. Pair with `onValueChange`. */
    value?: string;
    /** Fires with the new method id when the shopper picks a method. */
    onValueChange?: (value: string) => void;
    /** Disable the whole group. */
    disabled?: boolean;
    /** Opens the shipping-method details modal for the given method. Footer hidden when omitted. */
    onShowDetails?: (methodId: string) => void;
    className?: string;
    'data-testid'?: string;
}

/**
 * Shipping-method radio group for the checkout "Shipping Method" step — the
 * Figma delivery-method card design (node 34613-66829) wired to the basket's
 * applicable shipping methods.
 *
 * Renders one {@link RadioDeliveryMethodTile} per method inside a
 * {@link RadioGroup}, formatting the price and mapping the footer "Details"
 * links to the shipping-method details modal. Works controlled (`value` +
 * `onValueChange`) or uncontrolled (`defaultValue`); the selected tile's border
 * is highlighted to mirror the checked radio.
 */
export default function ReduceShippingMethods({
    methods,
    name = 'shippingMethodId',
    defaultValue,
    value,
    onValueChange,
    disabled = false,
    onShowDetails,
    className,
    'data-testid': dataTestId = 'reduce-shipping-methods',
}: ReduceShippingMethodsProps) {
    const { t, i18n } = useTranslation('checkout');
    const currency = useCurrency();
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState(defaultValue);
    const selectedValue = isControlled ? value : internalValue;

    const handleValueChange = (next: string) => {
        if (!isControlled) {
            setInternalValue(next);
        }
        onValueChange?.(next);
    };

    return (
        <RadioGroup
            value={isControlled ? value : undefined}
            defaultValue={isControlled ? undefined : defaultValue}
            onValueChange={handleValueChange}
            name={name}
            disabled={disabled}
            aria-label={t('shippingOptions.title')}
            className={cn('flex flex-col gap-3', className)}
            data-testid={dataTestId}>
            {methods.map((method) => (
                <RadioDeliveryMethodTile
                    key={method.id}
                    control="radio"
                    status="info"
                    value={method.id}
                    title={method.name}
                    price={
                        method.price === 0
                            ? t('shippingOptions.free')
                            : formatCurrency(method.price, i18n.language, currency)
                    }
                    description={method.description ?? ''}
                    selected={selectedValue === method.id}
                    disabled={disabled}
                    hideFooter={!onShowDetails}
                    onDeliveryDetails={onShowDetails ? () => onShowDetails(method.id) : undefined}
                    onFreeDetails={onShowDetails ? () => onShowDetails(method.id) : undefined}
                    radioTestId={`shipping-method-radio-${method.id}`}
                    data-testid={`shipping-method-${method.id}`}
                />
            ))}
        </RadioGroup>
    );
}
